import { useCreateSkillAction } from "@/action/use-create-skill-action";
import {
  GroupedTable,
  GroupedTableColumn,
} from "@/components/GroupedTable/GroupedTable";
import { Page, PageBody, PageHeader } from "@/components/app-layout";
import { KitDeletionDialog } from "@/components/dependency-deletion/KitDeletionDialog";
import { KitDisplayName } from "@/components/display/display-name/KitDisplayName";
import {
  BulkCreateSkillButton,
  SkillCategorySelect,
  type SkillCategory,
} from "@/components/skill";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Column, Row } from "@/components/ui/flex";
import { Input } from "@/components/ui/input";
import { useGroupSkillsData } from "@/data/use-group-skills-data";
import { useKitsData } from "@/data/use-kits-data";
import { useUsersData } from "@/data/use-users-data";
import { useGroupSlug } from "@/hooks/use-group-slug";
import { useGroupUuid } from "@/hooks/use-group-uuid";
import { groupByDate, smartDate } from "@/lib/date";
import { GetKitDto, groupKitSkillLinkControllerLink } from "@/lib/gen-api";
import { getInitials } from "@/lib/string";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { useState } from "react";
import { toast } from "sonner";
import { Label } from "../../components/ui/label";

export default function SkillsPage() {
  const groupUuid = useGroupUuid();
  const groupSlug = useGroupSlug();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [skillName, setSkillName] = useState("");
  const [selectedCategory, setSelectedCategory] =
    useState<SkillCategory>("Analysis");

  const { kits } = useKitsData(groupUuid, {
    type: "skill",
  });

  const { create: createSkill, isCreatingSkill } = useCreateSkillAction();

  const groupedSkills = groupByDate(kits, "date_updated");

  const columns: GroupedTableColumn<GetKitDto>[] = [
    { key: "name", label: "Name" },
    { key: "date_updated", label: "Last Modified" },
    { key: "user_uuid", label: "Created By" },
  ];

  async function handleCreateSkill() {
    if (skillName.trim()) {
      const name = skillName.trim();

      await createSkill({
        name,
        category: selectedCategory,
      });

      setSkillName("");
      setIsDialogOpen(false);
    }
  }

  return (
    <Page>
      <PageHeader>
        <PageHeader.Title>Workspace Skills</PageHeader.Title>
      </PageHeader>
      <PageBody>
        <Column>
          <p>
            Skills are like quick commands for your Expert AIs. You can create
            custom instructions here for actions like 'Analyze this report,'
            'Brainstorm campaign ideas,' or 'Draft a press release.' These save
            time and make sure your AIs always follow your best practices
            exactly. You can define what each Skill does, link it to specific
            data, and share it across your teams. Plus, you’ll see which of your
            Expert AIs are using them.
          </p>
        </Column>
        <Row className="gap-2">
          <BulkCreateSkillButton />
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => setIsDialogOpen(true)}>
                Create Skill
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>Create New Skill</DialogTitle>
                <DialogDescription>
                  Enter a name and category for your new skill.
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label htmlFor="skill-name">Name</Label>
                  <Input
                    id="skill-name"
                    value={skillName}
                    onChange={(e) => setSkillName(e.target.value)}
                    placeholder="Name"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleCreateSkill();
                      }
                    }}
                  />
                </div>
                <SkillCategorySelect
                  value={selectedCategory}
                  onValueChange={setSelectedCategory}
                  className="grid gap-2"
                />
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                  size="sm"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  onClick={handleCreateSkill}
                  size="sm"
                  disabled={isCreatingSkill}
                >
                  {isCreatingSkill ? "Creating..." : "Create Skill"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </Row>
        <GroupedTable
          data={groupedSkills}
          columns={columns}
          itemIdKey="uuid"
          renderCell={{
            name: (_, row) => <KitDisplayName kit={row} />,
            user_uuid: (value) => <UserCell userUuid={value} />,
            date_updated: (value) => smartDate(value),
          }}
          renderActions={({ row }) => (
            <KitDeletionDialog kitUuid={row.uuid} groupUuid={row.group_uuid}>
              <Button
                variant="ghost"
                size="sm"
                className="hover:bg-destructive hover:text-destructive-foreground"
                aria-label="Delete"
                title="Delete"
              >
                <FontAwesomeIcon icon={faTrash} />
              </Button>
            </KitDeletionDialog>
          )}
          rowLink={(row) => ({
            to: "/w/$groupSlug/k/$kitUuid/skill",
            params: { groupSlug, kitUuid: row.uuid },
          })}
          emptyMessage="No custom skills here yet. Create your first one to give your Expert AIs quick commands for important tasks."
          checkedActions={(selectedItems) => (
            <CreateCollectionFromSkillsButton selectedItems={selectedItems} />
          )}
        />
      </PageBody>
    </Page>
  );
}

function UserCell({ userUuid }: { userUuid: string }) {
  const groupUuid = useGroupUuid();
  const { getUserForUserUuid } = useUsersData(groupUuid);
  return (
    <Row className="items-center gap-2">
      <Avatar className="h-5 w-5">
        <AvatarFallback>
          {getInitials(getUserForUserUuid(userUuid)?.name || "", 2)}
        </AvatarFallback>
        <AvatarImage
          src={getUserForUserUuid(userUuid)?.photo_url ?? undefined}
        />
      </Avatar>
      <span>{getUserForUserUuid(userUuid)?.name}</span>
    </Row>
  );
}

function CreateCollectionFromSkillsButton({
  selectedItems,
}: {
  selectedItems: Map<string, GetKitDto>;
}) {
  const groupUuid = useGroupUuid();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [collectionName, setCollectionName] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  const { createKit } = useKitsData(groupUuid, { type: "collection" });
  const { skills } = useGroupSkillsData({
    groupUuid,
  });

  function resetDialog() {
    setIsDialogOpen(false);
    setCollectionName("");
  }

  async function handleCreateCollection() {
    const name = collectionName.trim();
    if (!name || !groupUuid) {
      return;
    }

    // Rows are "skill" kits (selectedItems keys are kit uuids), but linking a
    // skill into a collection requires the underlying Skill entity's own
    // uuid, which is a separate record. `creatorKitUuid` maps back to it.
    const skillUuids = skills
      .filter(
        (skill) =>
          !!skill.creatorKitUuid && selectedItems.has(skill.creatorKitUuid),
      )
      .map((skill) => skill.uuid);

    setIsCreating(true);
    try {
      const kit = await createKit({ name });
      if (!kit) {
        toast.error("Failed to create collection. Please try again.");
        return;
      }

      await Promise.all(
        skillUuids.map((skillUuid) =>
          groupKitSkillLinkControllerLink({
            path: { groupUuid, kitUuid: kit.uuid, skillUuid },
          }),
        ),
      );

      toast.success("Collection created successfully!");
      resetDialog();
    } catch {
      toast.error("Failed to create collection. Please try again.");
    } finally {
      setIsCreating(false);
    }
  }

  return (
    <Dialog
      open={isDialogOpen}
      onOpenChange={(open) => (open ? setIsDialogOpen(true) : resetDialog())}
    >
      <DialogTrigger asChild>
        <Button size="sm">Create Collection</Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Create New Collection</DialogTitle>
          <DialogDescription>
            Name your Collection. It will include the {selectedItems.size}{" "}
            selected Skill{selectedItems.size === 1 ? "" : "s"}.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="collection-name">Name</Label>
            <Input
              id="collection-name"
              value={collectionName}
              onChange={(e) => setCollectionName(e.target.value)}
              placeholder="Name"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleCreateCollection();
                }
              }}
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={resetDialog}
            size="sm"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            onClick={handleCreateCollection}
            size="sm"
            disabled={!collectionName.trim() || isCreating}
          >
            {isCreating ? "Creating..." : "Create Collection"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
